import axios from "axios";
import { ThunkExtra } from "@shared/models/request";
import { ExtractionResult, UploadUrlResponse } from "@investorRequest/models/bankExtraction";

export interface ExtractDocumentParams {
  file: File;
  paymentMethod: string;
  signal?: AbortSignal;
  onStepChange?: (step: number) => void;
}

export async function requestUploadUrl(
  request: ThunkExtra,
  contentType: string,
  signal?: AbortSignal
): Promise<UploadUrlResponse> {
  const response = await request<UploadUrlResponse, { contentType: string }>("/bank-extraction/v1/upload-url", {
    method: "post",
    body: { contentType },
    signal,
  });

  return response.data;
}

export async function uploadFileToS3(upload: UploadUrlResponse, file: File, signal?: AbortSignal): Promise<void> {
  const formData = new FormData();

  Object.entries(upload.fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  formData.append("file", file);

  await axios.post(upload.url, formData, {
    signal,
    withCredentials: false,
  });
}

export async function requestExtract(
  request: ThunkExtra,
  objectKey: string,
  paymentMethod: string,
  signal?: AbortSignal
): Promise<ExtractionResult> {
  const response = await request<ExtractionResult, { objectKey: string; paymentMethod: string }>(
    "/bank-extraction/v1/extract",
    {
      method: "post",
      body: { objectKey, paymentMethod },
      signal,
    }
  );

  return response.data;
}

export async function extractDocument(
  request: ThunkExtra,
  { file, paymentMethod, signal, onStepChange }: ExtractDocumentParams
): Promise<{ result: ExtractionResult; objectKey: string }> {
  onStepChange?.(0);
  const uploadUrl = await requestUploadUrl(request, file.type, signal);

  onStepChange?.(1);
  await uploadFileToS3(uploadUrl, file, signal);

  onStepChange?.(2);
  const result = await requestExtract(request, uploadUrl.objectKey, paymentMethod, signal);

  onStepChange?.(3);

  return { result, objectKey: uploadUrl.objectKey };
}

export default {
  requestUploadUrl,
  uploadFileToS3,
  requestExtract,
  extractDocument,
};
